import { Injectable, Signal, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EnumerationTypeDto } from '@digiwall/core/proxy';
import { DocRole, DwActiveLookupRequestDto } from '@digiwall/goliath-core/proxy';
import { CustomerService } from '@digiwall/goliath-contacts/proxy';
import { ArticleLookupRequestDto, ArticleService } from '@digiwall/goliath-products/proxy';
import { WorkOrderDayBookDto, WorkOrderDayBookService } from '@digiwall/goliath-documents/proxy';
import { GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';

/**
 * Option sources for the ServiceWorkOrder planning filters, shared by the config filter panel (preset
 * selection) and the board filter (interactive controls). InterventionType comes from the enumeration
 * store (no lookup call); DayBook is the full role-scoped list; Customer and Article are wired through
 * `ng-select` + the `dwApiSelect` directive for lazy/typeahead over large lists.
 */
@Injectable({ providedIn: 'root' })
export class ServiceWorkOrderFilterOptionsService {
  private readonly store = inject(GoliathCoreEnumerationTypeStore);
  private readonly dayBookService = inject(WorkOrderDayBookService);
  private readonly customerService = inject(CustomerService);
  private readonly articleService = inject(ArticleService);

  /** ServiceWorkOrderKind enumeration values (label/colour resolved from the store). */
  readonly interventionTypes: Signal<EnumerationTypeDto[]> = this.store.serviceWorkOrderKinds;

  /** Day-books usable on ServiceWorkOrder documents. */
  loadDayBooks(): Observable<WorkOrderDayBookDto[]> {
    return this.dayBookService.getAllByRole(DocRole.ServiceWorkOrder);
  }

  /** Lazy customer lookup for `[dwApiSelect]`. */
  readonly customerSelect = (dto: DwActiveLookupRequestDto<string>) => {
    dto.onlyActive = true;
    dto.sorting = [{ property: 'name' }];
    dto.queryProperties = ['code', 'name'];
    return this.customerService.getLookupList(dto);
  };

  /** Lazy article lookup for `[dwApiSelect]`. */
  readonly articleSelect = (dto: DwActiveLookupRequestDto<string>) => {
    dto.onlyActive = true;
    dto.sorting = [{ property: 'code' }];
    dto.queryProperties = ['code', 'name'];
    return this.articleService.getLookupList(dto as ArticleLookupRequestDto);
  };
}
